import {
  Component, ElementRef, HostListener, computed, effect, input, linkedSignal, output, signal, viewChild
} from '@angular/core';
import {InviteDelivery, looksLikeEmail} from '../access/grant.models';
import {InviteDeliveryChoiceComponent} from '../access/invite-delivery-choice.component';

/** What the dialog answers with when its primary action is pressed. */
export interface EmployeeInviteRequest {
  /** Whether to mail the invitation to the employee's stored address. */
  deliverByEmail: boolean;
  /** Whether only that address may redeem it — `BindToEmployeeEmail` **and** `EnforceBoundEmail` together. */
  bindToEmail: boolean;
}

/**
 * „Einladung erstellen" for one employee, as a modal.
 *
 * ### Why a dialog and not the inline block it replaces
 *
 * The Anmeldung card carries three unrelated things — the linked authenticators, the invitation, and the
 * password-login switch — and the invitation was the only one of them that asked questions. Inline, its two
 * controls sat permanently open on a card nobody visits to issue an invitation, competing with the list of
 * invitations directly below them and reading as settings of the card rather than as one act with a button.
 * A modal makes the act explicit: the card keeps a heading, a sentence and a button, and the questions appear
 * only for somebody who has said they want to answer them.
 *
 * It is also what the other two invite surfaces already do with their one-shot moments (the code dialog, the
 * re-send confirmation), so the card now follows the same rhythm: press a button, answer in a panel, get a
 * result.
 *
 * ### The shell is the style guide's, and the height treatment is #585's
 *
 * Overlay `fixed inset-0 z-[1100]`, panel `w-full … sm:max-w-lg … rounded-2xl`, `role="dialog"` +
 * `aria-modal` + `aria-labelledby`. The panel is a **flex column with a scrolling body and a footer outside
 * the scroll container**, because the body grows: with a bound address it carries the delivery choice, the
 * binding row and its three-sentence explanation, and on a short window a panel that scrolls as a whole hides
 * its own primary action below the fold — which is exactly how the invitation dialog's close button once went
 * unreachable.
 *
 * ### What it does NOT do
 *
 * It issues nothing. It collects two answers and emits them; the card owns the call, the busy flag, the
 * delivery outcome and the code dialog, because those outlive the dialog — the success path CLOSES it and then
 * reports. Keeping the HTTP call out here is what lets the failure path be the honest one: the dialog stays
 * open with the server's sentence in it, still holding what the issuer typed.
 *
 * ### Cancelling is always safe
 *
 * Nothing has been issued while this is open, so a backdrop click and `Escape` are ordinary cancels — the same
 * asymmetry `ResendConfirmDialogComponent` documents against `InviteCodeDialogComponent`, which blocks both
 * because its secret is unrecoverable. The caret starts on the **first control** rather than on Cancel, for
 * the same reason: this dialog asks a question, it does not warn about a consequence.
 */
@Component({
  selector: 'company-employee-invite-dialog',
  standalone: true,
  imports: [InviteDeliveryChoiceComponent],
  template: `
    <!-- Backdrop click cancels: nothing is issued while this is open, so it is the safe direction. -->
    <div class="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
         (click)="cancel()">
      <!-- A flex COLUMN capped to the viewport (#585), not one scrolling box: the footer carries the primary
           action and must never fall below the fold. dvh rather than vh, because a mobile browser's toolbars
           make vh taller than what is actually visible. -->
      <div class="flex max-h-[100dvh] w-full flex-col rounded-t-2xl bg-white shadow-xl
                  sm:max-h-[calc(100dvh-2rem)] sm:max-w-lg sm:rounded-2xl"
           role="dialog" aria-modal="true" aria-labelledby="employee-invite-dialog-heading"
           (click)="$event.stopPropagation()">
        <!-- min-h-0 is load-bearing: a flex item defaults to min-height:auto, refuses to shrink below its
             content, and would push the footer off screen exactly as a whole-panel scroll does. -->
        <div class="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">

          <div>
            <h2 id="employee-invite-dialog-heading" class="text-xl font-bold tracking-tight text-gray-900">
              Einladung erstellen
            </h2>
            <p class="mt-1 text-sm text-gray-500">Für {{ employeeName() }}</p>
          </div>

          <!-- The SHARED delivery control (#568) — the same segments and the same words as the employee
               roster's create form and Zugänge. It takes the initial focus: this dialog asks a question, so
               the caret starts on the first thing being asked.

               The template ref sits on a wrapping div rather than on the component element, because a ref on a
               component resolves to the component INSTANCE; reading it back as an ElementRef would work and
               reads worse than one honest wrapper. (No backticks anywhere in this template: it is a template
               literal, and one would end the string here — style-guide gotcha.) -->
          <div #firstControl>
            <company-invite-delivery-choice class="block" name="employee-invite-delivery"
              [value]="delivery()" [emailAvailable]="canDeliverByEmail()"
              unavailableReason="Per E-Mail senden ist erst möglich, wenn für diesen Mitarbeiter eine E-Mail-Adresse hinterlegt ist."
              hint="Wir senden den Einladungslink an die hinterlegte Adresse. Kommt die E-Mail nicht an, zeigen wir Ihnen den Link an."
              (valueChange)="delivery.set($event)"/>
          </div>

          @if (canBindToEmail()) {
            <!-- ONE binding switch (#584), where there used to be a checkbox for the binding and a switch for
                 its strength. Their only distinguishable state — bound but not enforced — refuses nobody,
                 because the gate falls back to the code alone for any address the provider does not vouch for,
                 so the pair offered a choice between a rule and a rule that does nothing.

                 Off by default, and rarely right here: an employee-link code is usually handed over in person,
                 and an Apple „E-Mail-Adresse verbergen“ recipient could never redeem a bound one. Hence a hint
                 naming exactly that cost, and BOTH positions described, because the default is the one nobody
                 switches and therefore never reads.

                 The words sit in a sibling block, so the switch needs the explicit aria-labelledby/-describedby
                 association or it announces as an unnamed checkbox. Hint at text-gray-700: gray-600 at text-xs
                 sits under the WCAG AA floor, which axe calls "serious". -->
            <div class="flex items-start justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3">
              <span class="min-w-0">
                <span id="employee-invite-binding-label" class="block text-sm font-medium text-gray-900">
                  Nur mit {{ employeeEmail() }} einlösbar
                </span>
                <span id="employee-invite-binding-hint" class="block text-xs text-gray-700">
                  Eingeschaltet: nur ein Konto, dessen vom Anbieter bestätigte Adresse genau dieser entspricht,
                  kann die Einladung annehmen. Apple „E-Mail-Adresse verbergen“ wird abgelehnt. Ausgeschaltet:
                  jedes Konto kann sie annehmen.
                </span>
              </span>
              <label class="relative inline-flex shrink-0 cursor-pointer items-center">
                <input id="employee-invite-binding" type="checkbox" class="peer sr-only"
                       [checked]="bindToEmail()"
                       aria-labelledby="employee-invite-binding-label"
                       aria-describedby="employee-invite-binding-hint"
                       (change)="setBindToEmail($event)">
                <span class="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-0.5 after:top-0.5
                             after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all
                             peer-checked:bg-theme-primary-600 peer-checked:after:translate-x-5"></span>
              </label>
            </div>
          } @else {
            <!-- The same bordered row on the grey surface an unavailable control gets, rather than a loose
                 line: the panel keeps its shape when there is no address, and the sentence sits where the
                 switch would have been. The delivery choice above already names the missing address; this says
                 what it means for the OTHER decision, which is a different question: who may redeem it. -->
            <div class="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <p class="text-xs text-gray-700">
                Ohne hinterlegte E-Mail-Adresse ist die Einladung an keine Adresse gebunden. Jedes Konto kann
                sie mit dem Link einlösen.
              </p>
            </div>
          }

          <!-- The server's own refusal, in the body rather than the footer: it belongs to what was answered
               above it, and the footer must stay the same height so the primary action does not move under
               the cursor as the message appears. -->
          @if (error()) {
            <p class="rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">
              {{ error() }}
            </p>
          }
        </div>

        <div class="flex justify-end gap-3 border-t border-gray-100 p-4 sm:px-6">
          <button type="button" (click)="cancel()" [disabled]="busy()"
                  class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                         py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                         hover:border-gray-300 hover:bg-gray-50 active:scale-95
                         disabled:cursor-not-allowed disabled:opacity-30">
            Abbrechen
          </button>
          <button type="button" (click)="confirm()" [disabled]="busy()"
                  class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5
                         py-2.5 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100
                         transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl
                         active:scale-95 disabled:cursor-not-allowed disabled:opacity-30
                         disabled:grayscale">
            Einladung erstellen
          </button>
        </div>
      </div>
    </div>
  `
})
export class EmployeeInviteDialogComponent {
  /** Whose invitation this is — the dialog's subtitle, so the panel names the person it is about. */
  readonly employeeName = input.required<string>();

  /**
   * That employee's stored address, or nothing. The single input both decisions stand on: it decides whether
   * the mail can be offered at all and what the binding would narrow the invitation to.
   */
  readonly employeeEmail = input<string | null | undefined>();

  /** True while the issue call is in flight, so a second press cannot mint a second invitation. */
  readonly busy = input(false);

  /**
   * The server's refusal, in the caller's own German vocabulary. Present ⇒ the dialog stayed open, holding
   * what was answered, which is the whole reason the HTTP call lives in the card and not in here.
   */
  readonly error = input<string | null>(null);

  /** Confirmed: the caller issues the invitation and closes this on success. */
  readonly confirmed = output<EmployeeInviteRequest>();

  /** Cancel, backdrop click or Escape — all the same outcome, since nothing has been issued. */
  readonly cancelled = output<void>();

  private readonly firstControl = viewChild<ElementRef<HTMLElement>>('firstControl');

  /**
   * The issuer's answer on delivery — writable, but **seeded from the address** rather than defaulted to a
   * literal: with an address the mail is what this feature exists to make the ordinary case, and without one
   * it is impossible.
   *
   * A `linkedSignal` rather than a plain signal plus an effect, and the difference is visible: an effect runs
   * *after* the first render, so the panel would paint „Nur Link erzeugen" selected and then correct itself —
   * a flicker, and a first frame in which the focus below would land on the wrong segment. This computes the
   * seed as part of the first read instead, so the dialog is never briefly wrong.
   *
   * The two screens with an address *field* need a hand-rolled override instead, because a later keystroke
   * there must not re-arm a mail the issuer declined; here the address cannot change while the dialog is open.
   */
  protected readonly delivery = linkedSignal<InviteDelivery>(
    () => this.canDeliverByEmail() ? 'email' : 'link');

  /** Whether only the stored address may redeem it. Off by default — see the switch's own comment. */
  protected readonly bindToEmail = signal(false);

  /**
   * Whether „Per E-Mail senden" may be offered: there has to be an address the server would mail to.
   * `looksLikeEmail`, the same helper the other two screens use, so all three agree on what an address is.
   */
  protected readonly canDeliverByEmail = computed(() => looksLikeEmail(this.employeeEmail()));

  /** The binding stands on the same address, and the server refuses one it cannot honour. */
  protected readonly canBindToEmail = computed(() => this.canDeliverByEmail());

  constructor() {
    // The caret starts on the first thing being asked — this dialog poses a question rather than warning about
    // a consequence, so Cancel is the wrong place for it.
    //
    // The CHECKED radio, not the first one: a radio group's focus belongs on its selection, and the first
    // segment („Per E-Mail senden") is the one that is DISABLED when the employee has no address — focusing a
    // disabled input silently does nothing and would leave the caret on <body>.
    //
    // Deferred by a frame: the effect runs as part of the pass that CREATES this view, and the radios' checked
    // state is written by that same pass, so querying `:checked` synchronously reads the DOM one render too
    // early. Same shape as the card's focus-return, and testable the same way (fakeAsync + tick).
    effect(() => {
      const group = this.firstControl()?.nativeElement;
      if (!group) return;
      setTimeout(() => {
        const target = group.querySelector<HTMLInputElement>('input:checked')
          ?? group.querySelector<HTMLInputElement>('input:not([disabled])');
        target?.focus();
      });
    });
  }

  protected setBindToEmail(event: Event): void {
    this.bindToEmail.set((event.target as HTMLInputElement).checked);
  }

  protected confirm(): void {
    if (this.busy()) return;
    // Both flags are gated on the address here as well as in the template, so no ordering of clicks can
    // compose a request the server would refuse with `E_GRANT_INVITE_EMAIL_REQUIRED`.
    this.confirmed.emit({
      deliverByEmail: this.canDeliverByEmail() && this.delivery() === 'email',
      bindToEmail: this.canBindToEmail() && this.bindToEmail()
    });
  }

  protected cancel(): void {
    if (this.busy()) return;
    this.cancelled.emit();
  }

  /** Escape is an ordinary cancel: nothing has been issued, so there is nothing to lose by taking it. */
  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.cancel();
  }
}
